import React, { type ReactElement, useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEqual } from 'lodash';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import Grid from 'atom-components/grid';
import MarkerFilter, { MarkerAppliedFilter } from 'pages/marking/components/filters/marker-filter';
import {
  type FilterFormValues,
  filterFormSchema
} from 'pages/marking/subpages/marking-allocation-tab/allocate-responses-modal/filter-form/filter-form.schema';
import { filterFormDefaultValues } from './filter-form.schema';

type Props = {
  onSubmit: SubmitHandler<FilterFormValues>;
  onReset: () => void;
};

export default function FilterForm({ onSubmit, onReset }: Props): ReactElement {
  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(filterFormSchema),
    defaultValues: filterFormDefaultValues
  });
  const { handleSubmit, reset, watch } = methods;

  const triggerFormSubmission = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const triggerFormReset = useCallback(() => {
    reset(filterFormDefaultValues);
    onReset();
  }, [onReset, reset]);

  const watchedAllFields = watch();

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <MarkerFilter triggerFormSubmission={triggerFormSubmission} warnIfNotCurrentUser />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column colSpan={12}>
              <AppliedFilters
                canClear={!isEqual(watchedAllFields, filterFormDefaultValues)}
                onClear={triggerFormReset}
              >
                <MarkerAppliedFilter
                  triggerFormSubmission={triggerFormSubmission}
                  warnIfNotCurrentUser
                />
              </AppliedFilters>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </form>
    </FormProvider>
  );
}
